"use client";

import { useMemo, useCallback } from "react";
import { postHyperliquidInfo } from "@cede/utils";
import { SETTLEMENT_COINS } from "@/constants/airdrop-farming";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Decimal from "decimal.js";

interface SpotBalance {
  coin: string;
  hold: string;
  total: string;
}

interface SpotBalancesResponse {
  balances: SpotBalance[];
}

interface PerpBalancesResponse {
  crossMarginSummary: {
    accountValue: string;
    totalMarginUsed: string;
    totalNtlPos: string;
    totalRawUsd: string;
  };
  marginSummary: {
    accountValue: string;
    totalNtlPos: string;
    totalRawUsd: string;
    totalMarginUsed: string;
  };
  crossMaintenanceMarginUsed: string;
  withdrawable: string;
}

interface UseHyperliquidFarmingBalanceOptions {
  primaryWalletAddress: string | undefined;
  isUnifiedMode: boolean;
  settlementCoin: string;
  enabled: boolean;
}

export interface HyperliquidFarmingBalance {
  combinedUsdBalance: string;
  settlementCoinBalance: string;
  spotUsdc: string;
  spotUsdcForSwap: string;
  getSpotBalance: (coin: string) => string;
  fetchBalances: () => Promise<void>;
  reset: () => void;
  /** True while the initial balance fetch is in progress (no data yet). */
  isFetching: boolean;
  /** True once at least one fetch has completed. */
  hasFetched: boolean;
}

const DISABLED_BALANCE: HyperliquidFarmingBalance = {
  combinedUsdBalance: "0",
  settlementCoinBalance: "0",
  spotUsdc: "0",
  spotUsdcForSwap: "0",
  getSpotBalance: () => "0",
  fetchBalances: async () => {},
  reset: () => {},
  isFetching: false,
  hasFetched: false,
};

const QUERY_KEY_PREFIX = "hl-farming-balance";

async function fetchHlBalances(user: string, isUnified: boolean) {
  if (isUnified) {
    // Unified accounts: all funds live in spot, no need to call clearinghouseState
    const spotRes = await postHyperliquidInfo<SpotBalancesResponse>({
      type: "spotClearinghouseState",
      user,
    });
    return { spotBalances: spotRes?.balances ?? [], perpWithdrawable: "0" };
  }

  // Legacy (non-unified): need both spot + perps
  const [spotRes, perpRes] = await Promise.all([
    postHyperliquidInfo<SpotBalancesResponse>({ type: "spotClearinghouseState", user }),
    postHyperliquidInfo<PerpBalancesResponse>({ type: "clearinghouseState", user }),
  ]);

  const withdrawable = perpRes?.withdrawable ?? perpRes?.crossMarginSummary?.totalRawUsd ?? "0";
  return { spotBalances: spotRes?.balances ?? [], perpWithdrawable: withdrawable };
}

export function useHyperliquidFarmingBalance({
  primaryWalletAddress,
  isUnifiedMode,
  settlementCoin,
  enabled,
}: UseHyperliquidFarmingBalanceOptions): HyperliquidFarmingBalance {
  const queryClient = useQueryClient();

  const queryKey = [QUERY_KEY_PREFIX, primaryWalletAddress, isUnifiedMode] as const;

  const {
    data,
    isFetching: isQueryFetching,
    isSuccess,
  } = useQuery({
    queryKey,
    queryFn: () => fetchHlBalances(primaryWalletAddress!, isUnifiedMode),
    enabled: enabled && !!primaryWalletAddress,
    staleTime: 10_000, // 10s — balances change on trades/transfers
    refetchInterval: 30_000, // Auto-refresh every 30s
  });

  const spotBalances = data?.spotBalances ?? [];
  const perpWithdrawable = data?.perpWithdrawable ?? "0";

  const hlSpotAvailable = useMemo(() => {
    const usdcEntry = spotBalances.find((b) => b.coin === "USDC");
    return usdcEntry ? (parseFloat(usdcEntry.total) - parseFloat(usdcEntry.hold)).toFixed(6) : "0";
  }, [spotBalances]);

  const getSpotBalance = useCallback(
    (coin: string): string => {
      const balance = spotBalances.find((b) => b.coin === coin);
      if (!balance) return "0";
      const available = parseFloat(balance.total) - parseFloat(balance.hold);
      return available > 0 ? available.toFixed(6) : "0";
    },
    [spotBalances],
  );

  const combinedUsdBalance = useMemo(() => {
    try {
      if (!isUnifiedMode) {
        return new Decimal(perpWithdrawable || "0").toFixed(2);
      }
      let total = new Decimal(0);
      const SWAP_FEE_MARGIN = 0.003; // 0.3% per swap hop
      for (const coin of SETTLEMENT_COINS) {
        const raw = coin === "USDC" ? hlSpotAvailable || "0" : getSpotBalance(coin);
        const bal = new Decimal(raw);
        if (bal.lte(0)) continue;
        if (coin === settlementCoin) {
          total = total.plus(bal);
        } else if (coin === "USDC" || settlementCoin === "USDC") {
          total = total.plus(bal.mul(1 - SWAP_FEE_MARGIN));
        } else {
          total = total.plus(bal.mul(1 - SWAP_FEE_MARGIN * 2));
        }
      }
      return total.toFixed(2);
    } catch {
      return "0";
    }
  }, [isUnifiedMode, settlementCoin, hlSpotAvailable, perpWithdrawable, getSpotBalance]);

  const settlementCoinBalance = useMemo(() => {
    try {
      if (!isUnifiedMode) {
        return new Decimal(perpWithdrawable || "0").toFixed(2);
      }
      const raw = settlementCoin === "USDC" ? hlSpotAvailable || "0" : getSpotBalance(settlementCoin);
      return new Decimal(raw).toFixed(2);
    } catch {
      return "0";
    }
  }, [isUnifiedMode, settlementCoin, hlSpotAvailable, perpWithdrawable, getSpotBalance]);

  const spotUsdc = useMemo(() => {
    if (isUnifiedMode) return "0";
    try {
      return new Decimal(hlSpotAvailable || "0").toFixed(2);
    } catch {
      return "0";
    }
  }, [isUnifiedMode, hlSpotAvailable]);

  const spotUsdcForSwap = useMemo(() => {
    try {
      return new Decimal(hlSpotAvailable || "0").toFixed(2);
    } catch {
      return "0";
    }
  }, [hlSpotAvailable]);

  const fetchBalances = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: [QUERY_KEY_PREFIX, primaryWalletAddress] });
  }, [queryClient, primaryWalletAddress]);

  const reset = useCallback(() => {
    queryClient.removeQueries({ queryKey: [QUERY_KEY_PREFIX, primaryWalletAddress] });
  }, [queryClient, primaryWalletAddress]);

  if (!enabled) return DISABLED_BALANCE;

  return {
    combinedUsdBalance,
    settlementCoinBalance,
    spotUsdc,
    spotUsdcForSwap,
    getSpotBalance,
    fetchBalances,
    reset,
    isFetching: isQueryFetching,
    hasFetched: isSuccess,
  };
}
